/**
 * "Ödeme Kaydı Hatası" ortak akışı: hata penceresi + "Yeniden Dene" + kayıt hatası telemetrisi.
 *
 * Para/fiş zaten alınmış ama satış SQL'e yazılamamışsa kasiyer aynı isteği (aynı
 * IdempotencyKey) yeniden gönderebilir; sunucu mükerrer kayıt yerine ilk sonucu döndürür.
 *
 * Kurallar:
 * - Yeniden denemede başarı → ilk denemedeki başarıyla AYNI son adımlar çalışır
 *   (`onSaved`: masa kapatma, fiş, para çekmecesi, mutfak siparişi). Çağıranlar
 *   `onSaved`'i `runOnce` ile sarar: mutfak fişi hiçbir koşulda iki kez basılmaz.
 * - Deneme sayacı bu akışın kendi değişkenindedir (eski kodda React state closure'a
 *   takılıp hiç artmıyordu → sınır hiç işlemiyordu).
 * - İstek oluşturulamadan düşen hatada (istek yok) "Yeniden Dene" gösterilmez
 *   (eski kod `undefined` isteği POST ediyordu).
 * - Telemetri: pencere her açıldığında reportPosSaveError; aynı anahtar sonradan
 *   kaydedilirse outcome=1, kasiyer kaydetmeden kapatırsa outcome=2.
 */
import type { QuickSaleWithInvoiceResponse } from '../../../../services/salesService';
import {
  reportPosSaveError,
  reportPosSaveOutcome,
  type PosSaveErrorInput,
} from '../../../../services/posTelemetryService';
import { accessLockCode, accessLockMessage, clearPendingAtomicPayment } from './accessLockError';
import { removePendingAtomicPayment } from './pendingAtomicPayments';

export const MAX_ATOMIC_SAVE_ATTEMPTS = 3;

export type ShowAlertModalFn = (
  title: string,
  message: string,
  showQuickAdd?: boolean,
  onRetry?: (() => void) | null,
  /** Pencere "Yeniden Dene" seçilmeden kapandığında çağrılır */
  onDismiss?: (() => void) | null
) => void;

type SnackbarFn = (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;

/** Sunucu Success=false döndürdü: yanıtı (HTTP durumu, süre, uç nokta) hata ile taşır. */
export class AtomicSaveError extends Error {
  readonly response: QuickSaleWithInvoiceResponse;
  constructor(message: string, response: QuickSaleWithInvoiceResponse) {
    super(message);
    this.name = 'AtomicSaveError';
    this.response = response;
  }
}

/** Sunucu "bu ödeme zaten kaydedilmiş" dedi → satış sunucuda VAR. */
export const isAlreadySavedError = (err: any): boolean =>
  /zaten kaydedilmiş/i.test(String(err?.message ?? err?.response?.Message ?? ''));

// ── Kayıt sonrası adımları çalıştırılmış işlem anahtarları (tarayıcı başına) ──
// Sunucu aynı anahtar için saklı yanıtı (IsReplay / idempotency-status) döndürebilir;
// bu tarayıcı o anahtarın son adımlarını (mutfak, fiş, masa, çekmece) zaten çalıştırdıysa
// TEKRAR çalıştırılmaz. Adımlar BAŞLARKEN işaretlenir: sayfa yarıda kapansa bile mutfak
// fişi ikinci kez basılmaz (eksik kalan adım elle tamamlanır — tekrar basmaktan güvenli).
const POST_SAVE_DONE_KEY = 'gts_atomic_post_save_done';
const POST_SAVE_DONE_TTL_MS = 48 * 60 * 60 * 1000;
const POST_SAVE_DONE_MAX = 300;

const readPostSaveDone = (): Record<string, number> => {
  try {
    const raw = localStorage.getItem(POST_SAVE_DONE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const isPostSaveDone = (idempotencyKey: string | null | undefined): boolean =>
  !!idempotencyKey && !!readPostSaveDone()[idempotencyKey];

export const markPostSaveDone = (idempotencyKey: string | null | undefined): void => {
  if (!idempotencyKey) return;
  try {
    const now = Date.now();
    const entries = Object.entries(readPostSaveDone())
      .filter(([, ts]) => now - Number(ts) < POST_SAVE_DONE_TTL_MS)
      .sort((x, y) => Number(x[1]) - Number(y[1]))
      .slice(-(POST_SAVE_DONE_MAX - 1));
    const next: Record<string, number> = Object.fromEntries(entries);
    next[idempotencyKey] = now;
    localStorage.setItem(POST_SAVE_DONE_KEY, JSON.stringify(next));
  } catch { /* depolama kapalı: bellek içi koruma (started) yine geçerli */ }
};

/**
 * Kayıt sonrası adımları en fazla BİR kez çalıştırır: aynı closure içinde ikinci çağrı
 * atlanır; `idempotencyKey` verilirse bu tarayıcıda o anahtar için daha önce çalışmışsa
 * da atlanır (sunucu IsReplay / durum sorgusu yanıtı → adımlar tekrarlanmaz).
 */
export function runOnce<TArgs extends any[]>(
  fn: (...args: TArgs) => Promise<void>,
  label: string,
  idempotencyKey?: string | null
): (...args: TArgs) => Promise<void> {
  let started = false;
  return async (...args: TArgs) => {
    if (started || isPostSaveDone(idempotencyKey)) {
      console.warn(`[${label}] Kayıt sonrası adımlar zaten çalıştı — tekrar edilmedi (mutfak/fiş/masa).`);
      return;
    }
    started = true;
    markPostSaveDone(idempotencyKey);
    await fn(...args);
  };
}

/** Telemetrideki cihaz adı: pavo_rest_p2p | pavo_p2p | pavopay | beko | ingenico | inpos | hugin | yok */
export const telemetryFiscalDevice = (selectedFiscalDevice: string | null | undefined): string => {
  const d = (selectedFiscalDevice || '').toLowerCase();
  if (!d || d === 'harici_pos') return 'yok';
  if (d.startsWith('beko')) return 'beko';
  if (d.startsWith('ingenico')) return 'ingenico';
  if (d.startsWith('inpos')) return 'inpos';
  if (d.startsWith('hugin')) return 'hugin';
  if (d === 'pavopay') return 'pavopay';
  return d;
};

/** Kasiyer adı: e-posta GÖNDERİLMEZ. */
export const telemetryCashierName = (...names: Array<string | null | undefined>): string | null => {
  for (const n of names) {
    const v = (n || '').trim();
    if (v && !v.includes('@')) return v;
  }
  return null;
};

/** reportPosSaveError girdisinin akışa özgü (hata dışı) kısmı */
export type SaveTelemetryContext = Omit<
  PosSaveErrorInput,
  'errorMessage' | 'errorKind' | 'httpStatus' | 'durationMs' | 'attempt' | 'clientOutcome' | 'idempotencyKey'
>;

/** Hata nesnesinden telemetri alanları (AtomicSaveError ise sunucu yanıtından). */
const errorTelemetry = (err: any) => {
  const resp: QuickSaleWithInvoiceResponse | undefined = err instanceof AtomicSaveError ? err.response : undefined;
  return {
    errorMessage: String(err?.message || err || 'Bilinmeyen hata'),
    errorKind: resp?.ClientErrorKind ?? undefined,
    httpStatus: resp?.HttpStatus ?? err?.response?.status ?? null,
    durationMs: resp?.DurationMs ?? null,
    endpoint: resp?.Endpoint ?? null,
  };
};

export interface AtomicSaveFailureOptions<TReq extends { IdempotencyKey?: string }> {
  showAlertModal: ShowAlertModalFn;
  showSnackbar: SnackbarFn;
  /** Gönderilen istek; yoksa (istek oluşmadan hata) yeniden deneme sunulmaz */
  request: TReq | undefined;
  send: (request: TReq) => Promise<QuickSaleWithInvoiceResponse>;
  /** Kayıt başarılı olunca çalışacak son adımlar (runOnce ile sarılmış olmalı) */
  onSaved: (response: QuickSaleWithInvoiceResponse) => Promise<void>;
  /** Pencerenin ilk satırı, ör. "Nakit ödeme alındı ancak kayıt yapılamadı." */
  intro: string;
  logLabel: string;
  telemetry: SaveTelemetryContext;
  /** Erişim kilidinde (403) ek temizlik (ör. POS bekleme modalını kapat) */
  onAccessLocked?: () => void;
  maxAttempts?: number;
}

/**
 * İlk denemedeki kayıt hatasını işler: pencereyi açar, telemetriyi yollar,
 * "Yeniden Dene"yi bağlar. Hiçbir zaman hata fırlatmaz.
 */
export function handleAtomicSaveFailure<TReq extends { IdempotencyKey?: string }>(
  error: any,
  opts: AtomicSaveFailureOptions<TReq>
): void {
  const key = opts.request?.IdempotencyKey ?? null;
  const maxAttempts = opts.maxAttempts ?? MAX_ATOMIC_SAVE_ATTEMPTS;
  let attempt = 1;
  /** Sonuç (kaydedildi / kapatıldı) bildirildi mi */
  let settled = false;

  const lockedOut = (err: any): boolean => {
    if (!accessLockCode(err)) return false;
    settled = true;
    clearPendingAtomicPayment();
    opts.onAccessLocked?.();
    opts.showAlertModal('Süreniz Doldu', accessLockMessage(err), false, null);
    return true;
  };

  const report = (err: any) => {
    const t = errorTelemetry(err);
    reportPosSaveError({
      ...opts.telemetry,
      ...t,
      endpoint: t.endpoint ?? opts.telemetry.endpoint ?? null,
      idempotencyKey: key,
      attempt,
    });
  };

  const finishSaved = async (response: QuickSaleWithInvoiceResponse, viaRetry: boolean) => {
    settled = true;
    removePendingAtomicPayment(key);
    if (viaRetry) reportPosSaveOutcome(key, 1);
    try {
      await opts.onSaved(response);
    } catch (postErr) {
      // Satış kayıtlı; son adımlardan biri düştü. Kayıt hatası penceresi AÇILMAZ
      // (yeniden kayıt denemesi mutfak/masa adımlarını tekrarlatabilirdi).
      console.error(`[${opts.logLabel}] Kayıt sonrası adım hatası:`, postErr);
      opts.showSnackbar('Satış kaydedildi ancak sonraki adımlardan biri (masa/fiş/mutfak) tamamlanamadı. Lütfen kontrol edin.', 'warning');
    }
  };

  /** "zaten kaydedilmiş" yanıtı: satış sunucuda var, yanıtın ayrıntısı bilinmiyor. */
  const alreadySavedResponse = (err: any): QuickSaleWithInvoiceResponse => ({
    ...(err instanceof AtomicSaveError ? err.response : {}),
    Success: true,
    Message: String(err?.message || ''),
  });

  const onDismiss = () => {
    if (settled) return;
    settled = true;
    reportPosSaveOutcome(key, 2);
  };

  const show = (err: any) => {
    const canRetry = !!opts.request && attempt < maxAttempts;
    let tail: string;
    if (canRetry) {
      tail = attempt === 1
        ? `"Yeniden Dene" ile ödeme kaydını tekrar deneyebilirsiniz.\nÖdeme tekrar alınmayacak, sadece kayıt işlemi tekrarlanacaktır.`
        : `Deneme: ${attempt + 1}/${maxAttempts}\nYeniden deneyebilirsiniz.`;
    } else if (opts.request) {
      tail = `Maksimum deneme sayısına ulaşıldı. Kayıt bilgisi bu cihazda saklandı; sayfa yeniden açıldığında tekrar denenecek. Lütfen sistem yöneticinize başvurun.`;
    } else {
      tail = `Lütfen sistem yöneticinize başvurun.`;
    }
    opts.showAlertModal(
      'Ödeme Kaydı Hatası',
      `${opts.intro}\n\nHata: ${err?.message || 'Bilinmeyen hata'}\n\n${tail}`,
      false,
      canRetry ? retry : null,
      onDismiss
    );
  };

  const retry = async () => {
    if (settled || !opts.request) return;
    attempt += 1;
    console.log(`[RETRY] ${opts.logLabel} yeniden deneniyor (${attempt}/${maxAttempts})...`);
    let response: QuickSaleWithInvoiceResponse;
    try {
      response = await opts.send(opts.request);
      if (!response.Success) {
        throw new AtomicSaveError(
          response.Message || response.ErrorDetails || `${opts.logLabel} kaydı başarısız (retry)`,
          response
        );
      }
    } catch (retryErr: any) {
      if (isAlreadySavedError(retryErr)) {
        console.warn(`[RETRY] ${opts.logLabel}: sunucu satışı zaten kayıtlı bildirdi — son adımlar tamamlanıyor`);
        opts.showSnackbar('Bu ödeme sunucuda zaten kayıtlıydı; tekrar kaydedilmedi.', 'info');
        await finishSaved(alreadySavedResponse(retryErr), true);
        return;
      }
      console.error(`[RETRY] ${opts.logLabel} yeniden başarısız:`, retryErr);
      if (lockedOut(retryErr)) return;
      report(retryErr);
      show(retryErr);
      return;
    }
    console.log(`[RETRY] ${opts.logLabel} başarılı!`, response.ReferenceNumber);
    await finishSaved(response, true);
  };

  // ── İlk deneme hatası ──
  if (lockedOut(error)) return;
  if (opts.request && isAlreadySavedError(error)) {
    // Ör. geçici hata sonrası otomatik tekrar: ilk istek aslında commit etmiş.
    console.warn(`[${opts.logLabel}] Sunucu satışı zaten kayıtlı bildirdi — son adımlar tamamlanıyor`);
    opts.showSnackbar('Bu ödeme sunucuda zaten kayıtlıydı; tekrar kaydedilmedi.', 'info');
    void finishSaved(alreadySavedResponse(error), false);
    return;
  }
  report(error);
  show(error);
}
